import React, { useState, useEffect, useRef, useCallback } from "react";
import InputFieldwithSearch from "../inputfields/InputFieldwithSearch";
import DropdownItem from "./DropdownItem";
import { AnimatePresence } from "framer-motion";
import { getFirefoxCssFix } from "../commonfunctions/getFirefoxCssFix";
import DropdownShimmer from "./DropdownShimmer";

interface SearchWithDropdownProps {
  options?: string[] | Record<string, any>[];
  onSelect?: (value?: Record<string, any> | string, action?: boolean) => void;
  handleSearchValue?: (value: string, page_number: number) => void;
  id?: string;
  noSearch?: boolean;
  className?: string;
  style?: React.CSSProperties;
  isInfiniteDropdown?: boolean;
  fetchMoreData?: (value: string, isPageNumberChange?: boolean) => Promise<any>;
  testId?: string;
  dropdownTestId?: string;
  handelDynamicValue?: (value: Record<string, any>) => string;
  dropdownTopClass?: string;
  dynamicDropdown?: boolean;
  notAbsolute?: boolean;
  loading?: boolean;
}

const SearchWithDropdown: React.FC<SearchWithDropdownProps> = ({
  options,
  onSelect,
  handleSearchValue = () => null,
  handelDynamicValue = () => "",
  id,
  dropdownTopClass,
  noSearch,
  className,
  style,
  isInfiniteDropdown = false,
  fetchMoreData,
  testId,
  dropdownTestId,
  dynamicDropdown,
  notAbsolute,
  loading: externalLoading
}) => {
  // Initialize filteredOptions - if options are empty, set to undefined to allow shimmer to show
  const [filteredOptions, setFilteredOptions] = useState<(string | Record<string, any>)[] | undefined>(options);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loading, setLoading] = useState(dynamicDropdown ? dynamicDropdown : false);
  
  // Use external loading state if provided, otherwise use internal loading state
  const isLoading = externalLoading !== undefined ? externalLoading : loading;
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filterTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleFilterOptions = useCallback(
    (shouldShowLoader = false) => {
      if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);

      if (dynamicDropdown && shouldShowLoader && externalLoading === undefined) setLoading(true);
        filterTimeoutRef.current = setTimeout(() => {
          const filtered = options?.filter((option) =>
            typeof option === "string"
              ? option.toLowerCase().includes(searchTerm.toLowerCase())
              : handelDynamicValue(option).toLowerCase().includes(searchTerm.toLowerCase())
          ) as (string | Record<string, any>)[] | undefined;
          setFilteredOptions(filtered);
        }, 300);

      if (externalLoading === undefined) {
        loadingTimeoutRef.current = setTimeout(() => {
         setLoading(false);
        }, 600);
      }
    },
    [searchTerm, options, dynamicDropdown, handelDynamicValue, externalLoading]
  );
  useEffect(() => {
    handleFilterOptions(false); // Don't show loader on mount
  }, [handleFilterOptions]);
  
  // Update filteredOptions when options change
  useEffect(() => {
    // For internal search (no external loading), always update
    // For external loading, update when loading is false (API completed) or when options change while not loading
    if (externalLoading === undefined || externalLoading === false) {
      setFilteredOptions(options);
    }
  }, [options, externalLoading]);
  useEffect(() => {
    return () => {
      if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    };
  }, []);

  const handleSearch = (value: string) => {
    setSearchTerm(value.trim());
    handleFilterOptions(true); // Show loader on search
    handleSearchValue(value.trim(), 1);
    setHasMoreData(true);
  };

  const handleScroll = async () => {
    if (!dropdownRef.current || !isInfiniteDropdown || !fetchMoreData || !hasMoreData || isLoadingMore) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = dropdownRef.current;
    
    // Check if we're near the bottom (within 20px)
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Set loading state immediately when near bottom
      setIsLoadingMore(true);
      
      scrollTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await fetchMoreData(searchTerm || "", true);
          // Check if response indicates no more data
          if (response?.data?.total_count === 0) {
            setHasMoreData(false);
          }
        } finally {
          setIsLoadingMore(false);
        }
      }, 300);
    }
  };

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <AnimatePresence>
      <div
        style={style}
        className={`${className} ${dropdownTopClass ? dropdownTopClass : 'top-[85px]'} w-80 rounded-lg border border-P_light_active p-3.5 ${notAbsolute ? '' : 'absolute'} bg-white z-40`}
      >
        {!noSearch && (
          <InputFieldwithSearch
            className={className}
            handleSearchValue={handleSearch}
            testId={`${id}_search`}
          />
        )}
        <div
          ref={dropdownRef}
          onScroll={handleScroll}
          className={`overflow-y-scroll z-[50] overflow-x-hidden ${getFirefoxCssFix(
            "scroll"
          )} scroll-auto max-h-[240px] p-1 ${noSearch ? "" : "mt-2"}`}
        >
          {(() => {
            // Only show full shimmer when:
            // 1. Not in pagination mode (isLoadingMore is false)
            // 2. We have no options (initial load or new search)
            // 3. isLoading is true
            // During pagination, even if parent sets loading=true, we should show options with "Loading more..." at bottom
            const hasOptions = filteredOptions && filteredOptions.length > 0;
            return !noSearch && isLoading && !isLoadingMore && !hasOptions;
          })() ? (
            <DropdownShimmer count={5} />
          ) : filteredOptions?.length === 0 && !isLoading && (!options || options.length === 0) ? (
            <p className="text-body2 text-secondary_text pt-2.5">No data found.</p>
          ) : (
            <>
              {filteredOptions?.map((option, index) => {
                const optionText =
                  typeof option === "string" ? option : handelDynamicValue(option);

                return (
                  <DropdownItem
                    key={index}
                    id={`${id}`}
                    dropdownTestId={dropdownTestId}
                    option={optionText}
                    onSelect={() => onSelect?.(option, false)}
                    index={index}
                  />
                );
              })}
              {isLoadingMore && (
                <div className="text-center py-2 text-secondary_text text-sm">
                  Loading more...
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AnimatePresence>
  );
};

export default SearchWithDropdown;
